/**
 * Thin fetch wrapper.
 *
 * Authentication is a HttpOnly session cookie, so nothing here touches tokens.
 * What it does handle is the CSRF double-submit: the server sets a readable
 * `js_csrf` cookie, and every state-changing request echoes it in a header.
 */

const CSRF_COOKIE = "js_csrf";

export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }

  /** True when the user is not signed in, or their session expired. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

function readCookie(name: string): string {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : "";
}

async function errorFrom(response: Response): Promise<ApiError> {
  let detail = response.statusText || "Request failed";
  try {
    const body = (await response.json()) as { detail?: unknown };
    if (typeof body.detail === "string") {
      detail = body.detail;
    } else if (Array.isArray(body.detail)) {
      // FastAPI validation errors arrive as a list of field problems.
      const first = body.detail[0] as { msg?: string } | undefined;
      detail = first?.msg ?? detail;
    }
  } catch {
    // A non-JSON error body (a proxy timeout page, say) keeps the status text.
  }
  return new ApiError(response.status, detail);
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  const isMutation = method !== "GET" && method !== "HEAD";

  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (isMutation) headers["X-CSRF-Token"] = readCookie(CSRF_COOKIE);

  const response = await fetch(path, {
    method,
    headers,
    credentials: "same-origin",
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (!response.ok) throw await errorFrom(response);
  if (response.status === 204) return undefined as T;

  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  del: <T>(path: string) => request<T>("DELETE", path),
};
